/** @prettier */
import React, { ReactNode, useEffect, useState, useCallback } from "react";
import classNames from "classnames";
import { ListGroup, ListGroupItem, Panel } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Score from "./Score";
import ReviewQuestionList from "./ReviewQuestionList";
import {
    getTestPerformanceData,
    TestPerformanceData,
    retakeTest as retakeTestApi,
} from "../api";
import "./TestResults.css";
import moment from "moment";
import { displayTestMode } from "../models";
import { useHistory } from "react-router-dom";
import { TestReviewFilter } from "../Review/testReviewState";
import ListGroupLinkItem from "../../util/ListGroupLinkItem";

type TestResultsProps = {
    id: string;
};
const TestResults = ({ id }: TestResultsProps) => {
    const [data, setData] = useState<TestPerformanceData>();
    useEffect(() => {
        getTestPerformanceData(id).then(setData);
    }, [id]);

    const history = useHistory();
    const retakeTest = useCallback(async () => {
        const { uuid } = await retakeTestApi(id);
        history.push(`/tests/${uuid}/run`);
    }, [id, history]);

    if (!data) {
        return (
            <div className="tre-loading">
                <FontAwesomeIcon icon="spinner" size="4x" spin />
            </div>
        );
    }
    return (
        <div className="test-results">
            <div className="dashboard-header">
                <h3 className="dashboard-title">Test Results</h3>
            </div>
            <div className="tre-grid">
                <TestDetailsPane data={data} />
                <ScorePane data={data} />
                <ReviewPane data={data} />
                <QuestionListPane data={data} />
                <ActionsPane retakeTest={retakeTest} />
            </div>
        </div>
    );
};
export default TestResults;

type PaneProps = {
    title: string;
    children: ReactNode;
    className?: string;
    /** Whether to add padding: panes with ListGroups or Tables shouldn't add padding */
    flush?: boolean;
};
const Pane = ({ title, children, className, flush = false }: PaneProps) => (
    <div className={classNames("tre-pane", !flush && "padded", className)}>
        <h5>{title}</h5>
        <Panel>{children}</Panel>
    </div>
);

type TestPerformanceDataProp = {
    data: TestPerformanceData;
};

const formatData = (date: string) => moment(date).format("YYYY-MM-DD");

const TestDetailsPane = ({ data }: TestPerformanceDataProp) => (
    <Pane title="Details" flush>
        <ListGroup className="tre-details">
            <ListGroupItem className="tre-details-name">
                <FontAwesomeIcon icon={["far", "sticky-note"]} />
                {data.testName}
                {/** TODO: Rename button */}
            </ListGroupItem>
            <ListGroupItem>
                <FontAwesomeIcon icon={["far", "calendar-plus"]} />
                Created on {formatData(data.createdOn)}
            </ListGroupItem>
            <ListGroupItem>
                <FontAwesomeIcon icon={["far", "calendar-check"]} />
                Submitted on {formatData(data.submittedOn)}
            </ListGroupItem>
            {/* <ListGroupItem>
                <FontAwesomeIcon icon={["far", "list-alt"]} />
                Specialty &gt; Emergency Medicine &gt; Multiple
            </ListGroupItem> */}
            <ListGroupItem>
                <FontAwesomeIcon icon={["far", "clock"]} />
                {displayTestMode(data.testMode)}
            </ListGroupItem>
        </ListGroup>
    </Pane>
);

const ScorePane = ({ data }: TestPerformanceDataProp) => (
    <Pane flush title="Score" className="score-widget">
        <Score
            correct={data.correctQuestionCount}
            incorrect={data.incorrectQuestionCount}
            unanswered={data.unansweredQuestionCount}
        />
    </Pane>
);
// TODO: allow linking different subsets of questions
const ReviewPane = ({ data }: TestPerformanceDataProp) => {
    const history = useHistory();

    const reviewHref = `/tests/${data.id}/review`;
    const listItemProps = (
        filter: TestReviewFilter,
        count: number,
    ): ListGroupItem.ListGroupItemProps => ({
        // Using both an href and an onClick:
        //    the onClick behavior is preferred (doesn't trigger a refresh)
        //    but using href allows for behavior like "open in new tab"
        href: `${reviewHref}?filter=${filter}`,
        onClick(e) {
            if (count > 0) {
                history.push(reviewHref, { filter });
            }
            e.preventDefault();
        },
        disabled: count === 0,
    });
    return (
        <Pane title="Start Review" flush>
            <ListGroup className="tre-start-review">
                <ListGroupItem
                    {...listItemProps("ALL", data.totalQuestionCount)}
                >
                    <FontAwesomeIcon icon="eye" />
                    Entire Test ({data.totalQuestionCount})
                </ListGroupItem>
                <ListGroupItem
                    {...listItemProps("CORRECT", data.correctQuestionCount)}
                >
                    <FontAwesomeIcon icon={["far", "check-circle"]} />
                    Only Correct Questions ({data.correctQuestionCount})
                </ListGroupItem>
                <ListGroupItem
                    {...listItemProps("INCORRECT", data.incorrectQuestionCount)}
                >
                    <FontAwesomeIcon icon={["far", "times-circle"]} />
                    Only Incorrect Questions ({data.incorrectQuestionCount})
                </ListGroupItem>
                <ListGroupItem
                    {...listItemProps(
                        "UNANSWERED",
                        data.unansweredQuestionCount,
                    )}
                >
                    <FontAwesomeIcon icon="exclamation-circle" />
                    Only Unanswered Questions ({data.unansweredQuestionCount})
                </ListGroupItem>
                <ListGroupItem
                    {...listItemProps(
                        "FLAGGED",
                        data.totalFlaggedQuestionCount,
                    )}
                >
                    <FontAwesomeIcon icon={["far", "flag"]} />
                    Only Flagged Questions ({data.totalFlaggedQuestionCount})
                </ListGroupItem>
            </ListGroup>
        </Pane>
    );
};
const QuestionListPane = ({ data }: TestPerformanceDataProp) => (
    <Pane className="tre-question-list" title="Question List" flush>
        <ReviewQuestionList
            testId={data.id}
            questions={data.testQuestionsPerformanceWS}
        />
    </Pane>
);
type ActionPaneProps = {
    retakeTest: () => void;
};
const ActionsPane = ({ retakeTest }: ActionPaneProps) => (
    <Pane title="More" flush>
        <ListGroup>
            <ListGroupItem onClick={retakeTest}>
                <FontAwesomeIcon icon="redo" />
                Retake Same Test
            </ListGroupItem>
            <ListGroupLinkItem href="/tests/create">
                <FontAwesomeIcon icon="plus" />
                Create New Test
            </ListGroupLinkItem>
            {/* <ListGroupItem>
                <FontAwesomeIcon icon={["far", "comment-alt"]} />
                Submit Test Feedback
            </ListGroupItem> */}
        </ListGroup>
    </Pane>
);
